<template>
  <div class="sa-page">
    <!-- STICKY TOPBAR WITH INTEGRATED SEARCH & FILTERS (MATCHES ADMIN / SUPERVISOR) -->
    <div class="dashboard-sticky-area">
      <div class="dashboard-topbar sa-topbar">
        <h4 class="dashboard-page-title">
          <CIcon :name="dashboard ? 'cil-speedometer' : 'cil-home'" class="dashboard-title-icon" />
          {{ dashboard ? text('ภาพรวมงานซ่อมนักศึกษา', 'Student Repair Dashboard') : text('ติดตามงานซ่อมนักศึกษา', 'Student Repair Tracking') }}
        </h4>

        <div class="dashboard-header-right">
          <!-- Global Search Box in Topbar -->
          <div class="dashboard-search-box" ref="searchBoxRef">
            <CIcon name="cil-search" class="search-icon" />
            <input
              type="text"
              v-model="searchQuery"
              :placeholder="text('ค้นหา...', 'Search...')"
              class="dashboard-search-input"
              @keydown="onKeyboardSearch" v-listbox-escape="closeKeyboardSearch"
              role="combobox" aria-autocomplete="list" :aria-controls="keyboardSearchId"
              :aria-expanded="String(Boolean(searchQuery && showSearchDropdown))"
              :aria-activedescendant="searchQuery && showSearchDropdown && keyboardSearchIndex >= 0 ? keyboardSearchId + '-' + keyboardSearchIndex : null"
              :aria-label="$t('common.search')"
              @focus="showSearchDropdown = true"
            />
            <button v-if="searchQuery" type="button" class="search-clear-btn" :aria-label="$t('common.clearSearch')" @click="clearSearch">
              <CIcon name="cil-x" />
            </button>

            <!-- Search Results Dropdown List -->
            <div
              v-if="searchQuery && showSearchDropdown"
              class="search-dropdown-menu" :id="keyboardSearchId" role="listbox" :aria-label="$t('common.search')"
            >
              <div class="search-dropdown-header">
                <span>{{ text('ผลการค้นหา (' + searchMatches.length + ' รายการ)', 'Search Results (' + searchMatches.length + ')') }}</span>
                <small v-if="searchMatches.length">{{ text('คลิกเพื่อไปยังรายการ', 'Click to jump to item') }}</small>
              </div>

              <div v-if="!searchMatches.length" class="p-3 text-center text-muted small">
                {{ text('ไม่พบข้อมูลที่ตรงกัน (ค้นหาได้ทั้งรหัสอาคาร, ชื่อไทย-อังกฤษ, ห้อง, หรือเลขที่แจ้ง)', 'No matches found (search by building, TH/EN name, room, or ticket #)') }}
              </div>

              <div
                v-for="(m, idx) in searchMatches"
                :key="m.id + '-' + idx"
                class="search-dropdown-item keyboard-listbox-option" :id="keyboardSearchId + '-' + idx" role="option" :aria-selected="String(keyboardSearchIndex === idx)" :data-keyboard-active="keyboardSearchIndex === idx" @mousedown.prevent
                @click="onSelectSearchResult(m)"
              >
                <span class="item-badge" :class="m.badgeClass">{{ m.sectionLabel }}</span>
                <div class="item-content">
                  <div class="item-title">{{ m.title }}</div>
                  <div class="item-desc">{{ m.description }}</div>
                  <small v-if="m.meta" class="text-muted d-block mt-1">{{ m.meta }}</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Date Range Filter Hotkeys (Matches Admin / Supervisor / Technician) -->
          <div class="dashboard-filter">
            <div v-if="selectedRange === 'custom'" class="custom-date-range">
              <input type="date" v-model="startDate" @change="onCustomDateChange" />
              <span>-</span>
              <input type="date" v-model="endDate" @change="onCustomDateChange" />
            </div>

            <div class="range-tabs">
              <button
                v-for="range in rangeOptions"
                :key="range.value"
                type="button"
                class="range-tab"
                :class="{
                  active: selectedRange === range.value,
                  'filter-tab': range.value === 'custom'
                }"
                @click="onRangeSelect(range.value)"
              >
                <CIcon v-if="range.value === 'custom'" name="cil-filter" />
                <span v-else>{{ range.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger" role="alert">{{ error }} <button type="button" class="sa-view-all-btn ml-2" @click="loadTickets">{{ text('ลองอีกครั้ง', 'Retry') }}</button></div>
    <p v-if="loading && !updatedAt" class="sa-panel" role="status">{{ text('กำลังโหลดงานซ่อม…', 'Loading repair requests…') }}</p>

    <template v-if="updatedAt">
      <template v-if="dashboard">
        <!-- KPI GRID (MATCHES ADMIN / SUPERVISOR / TECHNICIAN DESIGN TOKENS) -->
        <div class="kpi-grid sa-kpi-grid mb-4">
          <!-- Total -->
          <div class="summary-card" @click="goHome({})" v-keyboard-activate role="button" tabindex="0">
            <div class="summary-card-top">
              <div class="summary-icon icon-blue"><CIcon name="cil-bell" /></div>
            </div>
            <div class="summary-label">{{ text('คำร้องทั้งหมด', 'All Requests') }}</div>
            <div class="summary-value">{{ scopeTickets.length }}</div>
          </div>

          <!-- Pending -->
          <div class="summary-card" @click="goHome({ status: 'pending' })" v-keyboard-activate role="button" tabindex="0">
            <div class="summary-card-top">
              <div class="summary-icon icon-amber"><CIcon name="cil-clock" /></div>
            </div>
            <div class="summary-label">{{ statusLabel('pending') }}</div>
            <div class="summary-value">{{ count('pending') }}</div>
          </div>

          <!-- Assigned -->
          <div class="summary-card" @click="goHome({ status: 'assigned' })" v-keyboard-activate role="button" tabindex="0">
            <div class="summary-card-top">
              <div class="summary-icon icon-cyan"><CIcon name="cil-user" /></div>
            </div>
            <div class="summary-label">{{ statusLabel('assigned') }}</div>
            <div class="summary-value">{{ count('assigned') }}</div>
          </div>

          <!-- In Progress -->
          <div class="summary-card" @click="goHome({ status: 'in_progress' })" v-keyboard-activate role="button" tabindex="0">
            <div class="summary-card-top">
              <div class="summary-icon icon-purple"><CIcon name="cil-av-timer" /></div>
            </div>
            <div class="summary-label">{{ statusLabel('in_progress') }}</div>
            <div class="summary-value">{{ count('in_progress') }}</div>
          </div>
        </div>

        <!-- CHARTS & ANALYTICS -->
        <div class="sa-charts mb-4">
          <!-- 6 Months Trend -->
          <section class="sa-panel mb-0">
            <div class="sa-panel-header mb-3">
              <div>
                <h2><CIcon name="cil-av-timer" class="mr-1 text-primary" /> {{ text('แนวโน้มการแจ้งซ่อม', 'Repair request trend') }}</h2>
                <p class="text-muted mb-0">{{ trendSubtitle }}</p>
              </div>
            </div>
            <div class="sa-trend" :aria-label="text('จำนวนงานแจ้งซ่อม', 'Request counts')">
              <div v-for="item in trendItems" :key="item.key" class="sa-month">
                <div class="sa-month-bar-wrap">
                  <span class="sa-month-val">{{ item.count }}</span>
                  <div class="sa-month-bar" :style="{ height: Math.max(4, Math.min(80, Math.round((item.count / maxTrendCount) * 80))) + 'px' }"></div>
                </div>
                <span class="sa-month-lbl">{{ item.label }}</span>
              </div>
            </div>
          </section>

          <!-- Top Buildings (UNIFORM RANKING NUMBER STYLE ACROSS ALL RANKS) -->
          <section class="sa-panel mb-0">
            <div class="sa-panel-header mb-3">
              <div>
                <h2><CIcon name="cil-building" class="mr-1 text-primary" /> {{ text('อาคารที่แจ้งซ่อมมากที่สุด', 'Buildings with the most requests') }}</h2>
                <p class="text-muted mb-0">{{ text('5 อันดับตามตัวกรองที่เลือก', 'Top 5 matching filters') }}</p>
              </div>
              <span v-if="topBuildings.length" class="sa-header-badge">
                {{ topBuildings.length }} {{ text('อาคาร', 'buildings') }}
              </span>
            </div>

            <p v-if="!topBuildings.length" class="sa-empty">{{ text('ยังไม่มีข้อมูลอาคาร', 'No building data') }}</p>

            <div v-else class="sa-building-list">
              <button
                v-for="(building, idx) in topBuildings"
                :key="building.name"
                type="button"
                class="sa-building-card"
                :title="text('คลิกเพื่อดูงานในอาคารนี้', 'Click to filter requests in this building')"
                @click="goHome({ building: building.name })"
              >
                <div class="sa-building-left">
                  <span class="sa-rank-badge">{{ idx + 1 }}</span>
                  <CIcon :name="building.name === 'outdoor' ? 'cil-location-pin' : 'cil-building'" class="sa-building-icon" />
                  <span class="sa-building-title">{{ buildingLabel(building.name) }}</span>
                </div>

                <div class="sa-building-right">
                  <div class="sa-compact-bar-wrap" :title="buildingShareText(building.count)">
                    <div class="sa-compact-bar" :style="{ width: buildingPercent(building.count) + '%' }"></div>
                  </div>
                  <span class="sa-building-badge">
                    <b>{{ building.count }}</b> {{ text('งาน', 'jobs') }}
                    <small class="sa-pct-text">({{ buildingShareText(building.count) }})</small>
                  </span>
                  <span class="sa-row-chevron">›</span>
                </div>
              </button>
            </div>
          </section>
        </div>

        <!-- Requests Needing Follow-up -->
        <section class="sa-panel">
          <div class="sa-section-heading mb-3">
            <div>
              <h2><CIcon name="cil-list" class="mr-1 text-primary" /> {{ text('งานที่ควรติดตาม', 'Requests needing follow-up') }}</h2>
              <p class="text-muted mb-0">{{ text('งานที่เกินกำหนดซ่อม หรือซ่อมเสร็จแล้วรอนักศึกษาประเมิน', 'Overdue repairs or awaiting student rating') }}</p>
            </div>
            <button type="button" class="sa-view-all-btn" @click="goHome({})">{{ text('ดูงานทั้งหมด', 'View all') }}</button>
          </div>
          <p v-if="!attentionTickets.length" class="sa-empty">{{ text('ไม่มีงานเกินกำหนดหรือรอประเมินในช่วงที่เลือก', 'No overdue or unrated repairs match these filters') }}</p>
          <div v-else class="sa-followup-list">
            <router-link v-for="ticket in attentionTickets" :key="ticket._id" :to="detailPath(ticket)" class="sa-followup-card">
              <div class="sa-followup-info">
                <div class="sa-followup-head">
                  <strong class="sa-ticket-id">{{ ticket.ticketNumber }}</strong>
                  <span class="sa-dot">·</span>
                  <span class="sa-reporter">{{ reporter(ticket) }}</span>
                </div>
                <small class="sa-followup-desc">{{ ticket.userPart.problemDetails }}</small>
              </div>
              <span class="sa-badge" :class="isOverdue(ticket) ? 'sa-overdue' : 'sa-done'">
                {{ isOverdue(ticket) ? text('เกินกำหนดซ่อม (SLA)', 'Overdue') : statusLabel(ticket.status) }}
              </span>
            </router-link>
          </div>
        </section>
      </template>

      <!-- REDESIGNED HOME VIEW (CLEAN DATA TABLE, SEGMENTED TABS, IMPECCABLE UX) -->
      <section v-else class="sa-panel sa-home-section">
        <div class="sa-home-header">
          <div class="sa-tabs" :aria-label="text('กลุ่มสถานะงาน', 'Request groups')">
            <button v-for="tab in tabs" :key="tab.key" type="button" :aria-pressed="filters.tab === tab.key" :class="{ active: filters.tab === tab.key }" @click="selectTab(tab.key)">{{ text(tab.th, tab.en) }}</button>
          </div>
          <div class="sa-list-meta">
            <span role="status" class="sa-total-badge">{{ filteredTickets.length }} {{ text('รายการ', 'requests') }}</span>
          </div>
        </div>

        <p v-if="!filteredTickets.length" class="sa-empty">{{ tickets.length ? text('ไม่พบงานที่ตรงกับตัวกรอง', 'No matching requests') : text('ยังไม่มีงานแจ้งซ่อมจากผู้ใช้ที่ระบุสังกัดเป็นนักศึกษา', 'No repair requests from users with a student affiliation yet') }}</p>
        <div v-else class="sa-table-wrap">
          <table class="sa-table">
            <caption class="sr-only">{{ text('รายการแจ้งซ่อมของนักศึกษา', 'Student repair requests') }}</caption>
            <thead>
              <tr>
                <th scope="col">{{ text('เลขที่ใบแจ้งซ่อม', 'Ticket No.') }}</th>
                <th scope="col">{{ text('ผู้แจ้ง', 'Reporter') }}</th>
                <th scope="col">{{ text('ปัญหา', 'Problem') }}</th>
                <th scope="col">{{ text('สถานที่', 'Location') }}</th>
                <th scope="col">{{ text('วันที่แจ้ง', 'Reported Date') }}</th>
                <th scope="col">{{ text('สถานะ', 'Status') }}</th>
                <th scope="col">{{ text('ผู้รับผิดชอบ', 'Assigned to') }}</th>
                <th scope="col">{{ text('กำหนดซ่อม / ระยะเวลารอ', 'Deadline / Waiting time') }}</th>
                <th scope="col" class="text-right">{{ text('จัดการ', 'Action') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ticket in pageTickets" :key="ticket._id" :id="'sa-ticket-' + (ticket.ticketNumber || ticket._id)" :data-ticket-id="ticket._id" :data-ticket-no="ticket.ticketNumber">
                <td :data-label="text('เลขที่ใบแจ้งซ่อม', 'Ticket No.')">
                  <router-link :to="detailPath(ticket)" class="sa-ticket-number">{{ ticket.ticketNumber }}</router-link>
                </td>
                <td :data-label="text('ผู้แจ้ง', 'Reporter')">
                  <span class="sa-reporter-name">{{ reporter(ticket) }}</span>
                </td>
                <td :data-label="text('ปัญหา', 'Problem')">
                  <div class="sa-problem">{{ ticket.userPart.problemDetails }}</div>
                </td>
                <td :data-label="text('สถานที่', 'Location')">
                  <span class="sa-location">{{ location(ticket) }}</span>
                </td>
                <td :data-label="text('วันที่แจ้ง', 'Reported Date')">
                  {{ date(ticket.userPart.dateTimeInformed || ticket.createdAt) }}
                </td>
                <td :data-label="text('สถานะ', 'Status')">
                  <CBadge :color="getBadge(ticket.status)">{{ statusLabel(ticket.status) }}</CBadge>
                </td>
                <td :data-label="text('ผู้รับผิดชอบ', 'Assigned to')">
                  <span class="sa-assignee-name">{{ assignees(ticket) || text('ยังไม่มอบหมาย', 'Unassigned') }}</span>
                  <small class="sa-section-sub">{{ section(ticket, lang) }}</small>
                </td>
                <td :data-label="text('กำหนดซ่อม / ระยะเวลารอ', 'Deadline / Waiting time')">
                  <span :class="{ 'text-danger font-weight-bold': isOverdue(ticket) }">{{ ticket.slaDeadline ? date(ticket.slaDeadline) : text('ยังไม่ระบุกำหนด', 'No deadline set') }}</span>
                  <small v-if="isOverdue(ticket)" class="text-danger d-block font-weight-bold">{{ text('เกินกำหนดซ่อม (SLA)', 'Overdue') }}</small>
                  <small v-else-if="isOpen(ticket)" class="text-muted d-block">{{ text('รอมาแล้ว', 'Waiting') }} {{ waitingDays(ticket) }} {{ text('วัน', 'days') }}</small>
                </td>
                <td class="text-right">
                  <router-link class="sa-view-btn" :to="detailPath(ticket)">
                    {{ text('ดูงาน', 'View') }}
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <nav v-if="filteredTickets.length" class="sa-pagination" :aria-label="text('เปลี่ยนหน้ารายการ', 'Pagination')">
          <div class="sa-per-page-group d-flex align-items-center mr-auto">
            <label class="sa-per-page-label mb-0 mr-2 text-muted small">{{ text('แสดงต่อหน้า:', 'Items per page:') }}</label>
            <select v-model.number="pageSize" class="custom-select custom-select-sm" style="width: auto;" @change="page = 1">
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="50">50</option>
            </select>
          </div>
          <button type="button" class="btn btn-light" :disabled="page <= 1" @click="page--">{{ text('ก่อนหน้า', 'Previous') }}</button>
          <span>{{ text('หน้า', 'Page') }} {{ page }} / {{ totalPages }}</span>
          <button type="button" class="btn btn-light" :disabled="page >= totalPages" @click="page++">{{ text('ถัดไป', 'Next') }}</button>
        </nav>
      </section>
    </template>
  </div>
</template>

<script>
import moment from 'moment';
import keyboardActivate from '@/directives/keyboardActivate';
import listboxEscape from '@/directives/listboxEscape';
import keyboardSearch from '@/mixins/keyboardSearch';
import api from '@/service/api'
import { getBuildingLocalizedName } from '@/utils/buildingBilingual'
import { getTicketUserName, getTicketReporterAffiliation, getTicketLocationFull, getTicketSectionName, formatTicketDate } from '@/utils/ticketViewHelper'
import { translateJobType } from '@/utils/categoryi18n'
import { studentTicketStatuses, filterStudentTickets, studentTicketBuilding, studentTicketAssignees, isStudentTicketOpen, isStudentTicketOverdue } from '@/utils/studentAffairs'
import { matchTicketBilingual } from '@/utils/buildingBilingual'

const emptyFilters = () => ({ search: '', from: '', to: '', building: '', job: '', tab: 'active', status: '', attention: '' })

export default {
  directives: { keyboardActivate, listboxEscape },
  mixins: [keyboardSearch],
  name: 'StudentAffairsOverview',
  props: { dashboard: { type: Boolean, default: false } },
  data() {
    return {
      tickets: [],
      updatedAt: null,
      loading: false,
      error: '',
      filters: emptyFilters(),
      searchQuery: '',
      showSearchDropdown: false,
      page: 1,
      pageSize: 10,
      statuses: studentTicketStatuses,
      selectedRange: 'week',
      startDate: moment().startOf('isoWeek').format('YYYY-MM-DD'),
      endDate: moment().endOf('isoWeek').format('YYYY-MM-DD'),
      tabs: [
        { key: 'active', th: 'งานกำลังดำเนินการ', en: 'Active repairs' },
        { key: 'followup', th: 'งานที่ควรติดตาม', en: 'Follow-up' },
        { key: 'closed', th: 'งานที่เสร็จแล้ว/ยกเลิก', en: 'Closed/cancelled' },
        { key: 'all', th: 'งานทั้งหมด', en: 'All repairs' },
      ],
      mainStatuses: ['pending', 'assigned', 'in_progress', 'done', 'welldone'],
    }
  },
  computed: {
    rangeOptions() {
      return [
        { label: this.text('สัปดาห์', 'Week'), value: 'week' },
        { label: this.text('เดือน', 'Month'), value: 'month' },
        { label: this.text('ปี', 'Year'), value: 'year' },
        { label: this.text('กำหนดเอง', 'Custom'), value: 'custom' }
      ]
    },
    lang() { return this.$store.state.locale.lang },
    activeFilterCount() {
      let count = 0
      if (this.filters.from) count++
      if (this.filters.to) count++
      if (this.filters.building) count++
      if (this.filters.job) count++
      return count
    },
    hasActiveNonSearchFilters() {
      return Boolean(this.filters.from || this.filters.to || this.filters.building || this.filters.job)
    },
    scopeTickets() { return filterStudentTickets(this.tickets, this.filters) },
    overdue() { return this.scopeTickets.filter(ticket => this.isOverdue(ticket)) },
    attentionTickets() { return this.scopeTickets.filter(ticket => this.isOverdue(ticket) || ticket.status === 'done').slice(0, 10) },
    buildings() { return [...new Set(this.tickets.map(studentTicketBuilding).filter(Boolean))].sort() },
    jobs() { return [...new Set(this.tickets.map(ticket => ticket.userPart.jobType).filter(Boolean))].sort() },
    topBuildings() {
      const counts = {}
      this.scopeTickets.forEach(ticket => {
        const building = studentTicketBuilding(ticket)
        if (building) counts[building] = (counts[building] || 0) + 1
      })
      return Object.entries(counts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 5)
    },
    topBuildingsTotal() {
      return this.topBuildings.reduce((sum, b) => sum + b.count, 0) || this.scopeTickets.length || 1
    },
    trendSubtitle() {
      switch (this.selectedRange) {
        case 'year':
          return this.text('5 ปีย้อนหลัง · เฉพาะรายการที่ตรงกับตัวกรอง', 'Past 5 years · matching filters')
        case 'month':
          return this.text('12 เดือน (ม.ค. - ธ.ค.) · เฉพาะรายการที่ตรงกับตัวกรอง', '12 months (Jan - Dec) · matching filters')
        case 'week':
          return this.text('7 วันในสัปดาห์นี้ · เฉพาะรายการที่ตรงกับตัวกรอง', 'This week (7 days) · matching filters')
        default:
          return this.text('ช่วงเวลาที่เลือก · เฉพาะรายการที่ตรงกับตัวกรอง', 'Selected period · matching filters')
      }
    },
    trendItems() {
      const now = moment(this.updatedAt || Date.now())

      if (this.selectedRange === 'year') {
        const curYear = now.year()
        const years = [curYear - 4, curYear - 3, curYear - 2, curYear - 1, curYear]
        return years.map(y => {
          const count = this.scopeTickets.filter(ticket => {
            const tDate = moment(ticket.userPart?.dateTimeInformed || ticket.createdAt)
            return tDate.isValid() && tDate.year() === y
          }).length
          return {
            key: `year-${y}`,
            label: String(y),
            count
          }
        })
      }

      if (this.selectedRange === 'month') {
        const curYear = now.year()
        const thMonths = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
        const enMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        return thMonths.map((th, m) => {
          const count = this.scopeTickets.filter(ticket => {
            const tDate = moment(ticket.userPart?.dateTimeInformed || ticket.createdAt)
            return tDate.isValid() && tDate.year() === curYear && tDate.month() === m
          }).length
          return {
            key: `month-${m}`,
            label: this.text(th, enMonths[m]),
            count
          }
        })
      }

      if (this.selectedRange === 'week') {
        const startOfWeek = now.clone().startOf('isoWeek')
        const thDays = ['จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.', 'อา.']
        const enDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
        return thDays.map((th, i) => {
          const dayDate = startOfWeek.clone().add(i, 'days')
          const count = this.scopeTickets.filter(ticket => {
            const tDate = moment(ticket.userPart?.dateTimeInformed || ticket.createdAt)
            return tDate.isValid() && tDate.isSame(dayDate, 'day')
          }).length
          return {
            key: `day-${i}`,
            label: this.text(th, enDays[i]),
            count
          }
        })
      }

      // Default or custom fallback
      const thMonths = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
      const enMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const curYear = now.year()
      return thMonths.map((th, m) => {
        const count = this.scopeTickets.filter(ticket => {
          const tDate = moment(ticket.userPart?.dateTimeInformed || ticket.createdAt)
          return tDate.isValid() && tDate.year() === curYear && tDate.month() === m
        }).length
        return {
          key: `month-${m}`,
          label: this.text(th, enMonths[m]),
          count
        }
      })
    },
    maxTrendCount() {
      return Math.max(1, ...this.trendItems.map(item => item.count))
    },
    months() {
      return this.trendItems
    },
    maxMonth() {
      return this.maxTrendCount
    },
    filteredTickets() {
      return this.scopeTickets.filter(ticket => {
        if (this.filters.tab === 'active' && (ticket.status === 'done' || ticket.status === 'welldone' || ticket.status === 'cancelled')) return false
        if (this.filters.tab === 'followup' && !this.isOverdue(ticket) && ticket.status !== 'done') return false
        if (this.filters.tab === 'closed' && (ticket.status !== 'done' && ticket.status !== 'welldone' && ticket.status !== 'cancelled')) return false
        if (this.filters.status && ticket.status !== this.filters.status) return false
        if (this.filters.attention === 'overdue' && !this.isOverdue(ticket)) return false
        if (this.filters.attention === 'unrated' && ticket.status !== 'done') return false
        return true
      })
    },
    totalPages() { return Math.max(1, Math.ceil(this.filteredTickets.length / this.pageSize)) },
    pageTickets() {
      const start = (this.page - 1) * this.pageSize
      return this.filteredTickets.slice(start, start + this.pageSize)
    },
    searchMatches() {
      if (!this.searchQuery || !this.searchQuery.trim()) return []
      const q = this.searchQuery.trim()
      const allTickets = Array.isArray(this.tickets) ? this.tickets : []
      const matches = allTickets.filter(t => matchTicketBilingual(t, q))

      return matches.slice(0, 30).map((t, idx) => {
        const ticketNo = t.ticketNumber || (t._id || '').substring(0, 8).toUpperCase() || '-'
        const loc = this.location(t) || '-'
        const rawStatus = (t.status || '').toLowerCase()
        const prob = t.userPart?.problemDetails || '-'
        const reporterName = this.reporter(t) || '-'

        return {
          id: t._id || t.ticketNumber || idx,
          ticket: t,
          title: `${ticketNo} · ${loc}`,
          description: prob,
          meta: reporterName ? `${this.text('ผู้แจ้ง', 'Reporter')}: ${reporterName}` : '',
          sectionLabel: this.statusLabel(rawStatus),
          badgeClass: 'badge-' + this.getBadge(rawStatus)
        }
      })
    },
  },
  watch: {
    filters: { deep: true, handler() { this.page = 1 } },
    '$route.query': {
      immediate: true,
      handler(query) {
        if (query) {
          const { jumpTicket, ...rest } = query
          this.filters = { ...emptyFilters(), ...rest }
          if (jumpTicket) {
            this.$nextTick(() => {
              this.jumpToTicket(jumpTicket, jumpTicket)
            })
          }
        }
      },
    },
  },
  mounted() {
    if (this.dashboard) {
      if (!this.filters.from && this.startDate) this.filters.from = this.startDate
      if (!this.filters.to && this.endDate) this.filters.to = this.endDate
    }
    this.loadTickets()
    this.refreshTimer = setInterval(() => { if (!document.hidden && !this.loading) this.loadTickets() }, 60000)
    document.addEventListener('click', this.handleDocumentClick)
    if (this.$route.query && this.$route.query.jumpTicket) {
      this.$nextTick(() => {
        this.jumpToTicket(this.$route.query.jumpTicket, this.$route.query.jumpTicket)
      })
    }
  },
  beforeDestroy() {
    clearInterval(this.refreshTimer)
    document.removeEventListener('click', this.handleDocumentClick)
  },
  methods: {
    onRangeSelect(rangeValue) {
      const today = moment()

      if (rangeValue === 'custom') {
        if (this.selectedRange !== 'custom') {
          this.startDate = ''
          this.endDate = ''
          this.filters.from = ''
          this.filters.to = ''
        }
        this.selectedRange = 'custom'
        return
      }

      if (rangeValue === 'all') {
        this.startDate = ''
        this.endDate = ''
        this.filters.from = ''
        this.filters.to = ''
        this.selectedRange = 'all'
        return
      }

      switch (rangeValue) {
        case 'today':
          this.startDate = today.format('YYYY-MM-DD')
          this.endDate = today.format('YYYY-MM-DD')
          break
        case 'week':
          this.startDate = today.clone().startOf('isoWeek').format('YYYY-MM-DD')
          this.endDate = today.clone().endOf('isoWeek').format('YYYY-MM-DD')
          break
        case 'month':
          this.startDate = today.clone().startOf('year').format('YYYY-MM-DD')
          this.endDate = today.clone().endOf('year').format('YYYY-MM-DD')
          break
        case 'year':
          this.startDate = today.clone().subtract(4, 'years').startOf('year').format('YYYY-MM-DD')
          this.endDate = today.clone().endOf('year').format('YYYY-MM-DD')
          break
      }

      this.selectedRange = rangeValue
      this.filters.from = this.startDate
      this.filters.to = this.endDate
    },

    onCustomDateChange() {
      if (!this.startDate || !this.endDate) return
      this.filters.from = this.startDate
      this.filters.to = this.endDate
    },
    text(th, en) { return this.lang === 'en' ? en : th },
    reporter: getTicketUserName,
    affiliation: getTicketReporterAffiliation,
    section: getTicketSectionName,
    assignees: studentTicketAssignees,
    isOpen: isStudentTicketOpen,
    isOverdue(ticket) { return isStudentTicketOverdue(ticket, this.updatedAt || Date.now()) },
    statusLabel(status) { return (this.statuses[status] || [status, status])[this.lang === 'en' ? 1 : 0] },
    getBadge(status) {
      switch (status) {
        case 'pending': return 'warning'
        case 'assigned': return 'info'
        case 'in_progress': return 'primary'
        case 'done':
        case 'welldone': return 'success'
        case 'cancelled': return 'secondary'
        default: return 'light'
      }
    },
    date(value) { return formatTicketDate(value, this.lang) },
    location(ticket) { return ticket.userPart.locationType === 'outdoor' ? this.text('นอกอาคาร', 'Outdoors') : getTicketLocationFull(ticket, this.$t.bind(this), this.$store.state.locale.lang) },
    buildingLabel(building) { return building === 'outdoor' ? this.text('นอกอาคาร', 'Outdoors') : getBuildingLocalizedName(building, this.$store.state.locale.lang) },
    buildingPercent(count) {
      const total = this.topBuildingsTotal
      return Math.min(100, Math.max(8, Math.round((count / total) * 100)))
    },
    buildingShareText(count) {
      const total = this.topBuildingsTotal
      const pct = Math.round((count / total) * 100)
      return `${pct}%`
    },
    jobLabel(job) { return translateJobType(job, this.lang) },
    count(status) { return this.scopeTickets.filter(ticket => ticket.status === status).length },
    waitingDays(ticket) { return Math.max(0, Math.floor(((this.updatedAt || Date.now()) - new Date(ticket.userPart.dateTimeInformed || ticket.createdAt).getTime()) / 86400000)) },
    detailPath(ticket) { return { path: `/student-affairs/home/${ticket._id}`, query: { ...this.filters, source: this.dashboard ? 'dashboard' : 'home' } } },
    goHome(query) { this.$router.push({ path: '/student-affairs/home', query: { ...this.filters, tab: 'all', status: '', attention: '', ...query } }) },
    selectTab(tab) { this.filters.tab = tab; this.filters.status = ''; this.filters.attention = '' },
    resetFilters() { this.filters = emptyFilters() },
    async loadTickets() {
      if (this.loading) return
      this.loading = true
      try {
        const response = await api.ticket('get', null, { urlAppend: '/student-affairs' })
        this.tickets = response.data.data
        this.updatedAt = Date.now()
        this.error = ''
        if (this.$route.query && this.$route.query.jumpTicket) {
          this.$nextTick(() => {
            this.jumpToTicket(this.$route.query.jumpTicket, this.$route.query.jumpTicket)
          })
        }
      } catch (error) {
        this.error = this.text('โหลดข้อมูลไม่สำเร็จ กรุณาลองอีกครั้ง ข้อมูลเดิมอาจไม่เป็นปัจจุบัน', 'Unable to load requests. Please retry; previously loaded data may be out of date.')
      } finally { this.loading = false }
    },
    handleDocumentClick(e) {
      if (this.$refs.searchBoxRef && !this.$refs.searchBoxRef.contains(e.target)) {
        this.showSearchDropdown = false
      }
    },
    clearSearch() {
      this.searchQuery = ''
      this.showSearchDropdown = false
    },
    closeSearchDropdown() {
      this.showSearchDropdown = false
    },
    onSearchEnter() {
      if (this.searchMatches && this.searchMatches.length > 0) {
        this.onSelectSearchResult(this.searchMatches[0])
      }
    },
    onSelectSearchResult(m) {
      if (!m || !m.ticket) return
      this.showSearchDropdown = false
      const ticket = m.ticket
      const ticketId = ticket._id || ticket.id || ''
      const ticketNo = ticket.ticketNumber || (ticketId ? ticketId.substring(0, 8).toUpperCase() : '')

      if (this.dashboard) {
        this.$router.push({ path: '/student-affairs/home', query: { jumpTicket: ticketId || ticketNo } })
        return
      }

      this.jumpToTicket(ticketId, ticketNo)
    },
    jumpToTicket(ticketId, ticketNo) {
      this.filters.tab = 'all'
      this.filters.status = ''
      this.filters.attention = ''
      this.filters.search = ''

      this.$nextTick(() => {
        const list = this.filteredTickets || []
        const targetId = String(ticketId || '').toLowerCase()
        const targetNo = String(ticketNo || '').toLowerCase()

        const idx = list.findIndex(t => {
          const id = String(t._id || t.id || '').toLowerCase()
          const no = String(t.ticketNumber || (id ? id.substring(0, 8).toUpperCase() : '')).toLowerCase()
          return (targetId && id === targetId) || (targetNo && (no === targetNo || id.substring(0, 8).toUpperCase() === targetNo.toUpperCase()))
        })

        if (idx !== -1) {
          this.page = Math.floor(idx / this.pageSize) + 1
        }
        this.retryScrollToAndHighlight([
          'sa-ticket-' + ticketId,
          'sa-ticket-' + ticketNo,
          ticketId,
          ticketNo
        ])
      })
    },
    scrollElementToCenter(target) {
      if (!target) return
      const scrollContainers = [
        document.querySelector('.c-body'),
        document.querySelector('.c-main'),
        document.querySelector('.role-theme-app'),
        document.scrollingElement,
        document.documentElement,
        document.body
      ]
      const container = scrollContainers.find(
        c => c && c.scrollHeight > c.clientHeight && (typeof window === 'undefined' || window.getComputedStyle(c).overflowY !== 'hidden')
      )
      const bottomNavHeight = 90
      const headerHeight = 70

      if (container && container !== document.documentElement && container !== document.body) {
        const containerRect = container.getBoundingClientRect()
        const targetRect = target.getBoundingClientRect()
        const currentScroll = container.scrollTop
        const relativeTargetTop = targetRect.top - containerRect.top + currentScroll
        const effectiveHeight = Math.max(100, containerRect.height - bottomNavHeight)
        const idealTop = relativeTargetTop - (effectiveHeight / 2) + (targetRect.height / 2)

        container.scrollTo({
          top: Math.max(0, idealTop),
          behavior: 'smooth'
        })
      } else {
        const targetRect = target.getBoundingClientRect()
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0
        const windowHeight = window.innerHeight || document.documentElement.clientHeight
        const effectiveHeight = Math.max(100, windowHeight - headerHeight - bottomNavHeight)
        const idealTop = (currentScrollY + targetRect.top) - headerHeight - (effectiveHeight / 2) + (targetRect.height / 2)

        window.scrollTo({
          top: Math.max(0, idealTop),
          behavior: 'smooth'
        })
      }
    },

    scrollToAndHighlight(ids) {
      const isVisible = (el) => {
        if (!el) return false
        const rect = el.getBoundingClientRect()
        if (rect.width === 0 && rect.height === 0) return false
        if (el.offsetParent === null && (typeof window === 'undefined' || window.getComputedStyle(el).position !== 'fixed')) {
          return false
        }
        return true
      }

      for (const id of ids) {
        if (!id) continue
        const rawId = String(id).replace(/^sa-ticket-/, '')
        const candidates = [
          document.getElementById(id),
          document.getElementById('sa-ticket-' + rawId),
          ...(this.$el ? Array.from(this.$el.querySelectorAll(`[data-ticket-id="${rawId}"], [data-ticket-no="${rawId}"]`)) : [])
        ].filter(Boolean)

        for (const el of candidates) {
          const target = el.closest('.sa-followup-card') || el.closest('tr') || el
          if (isVisible(target)) {
            this.scrollElementToCenter(target)
            target.classList.remove('match-highlight-active')
            void target.offsetWidth
            target.classList.add('match-highlight-active')
            setTimeout(() => {
              target.classList.remove('match-highlight-active')
            }, 3600)
            return true
          }
        }
      }
      return false
    },
    retryScrollToAndHighlight(ids, attempts = 10) {
      this.$nextTick(() => {
        const found = this.scrollToAndHighlight(ids)
        if (!found && attempts > 0) {
          setTimeout(() => {
            this.retryScrollToAndHighlight(ids, attempts - 1)
          }, 100)
        }
      })
    },
  },
}
</script>

<style scoped>
.sa-page { color: #0f172a; }
.dashboard-sticky-area { margin-bottom: 20px; }

/* DATE RANGE FILTERS (MATCHES ADMIN / SUPERVISOR / TECHNICIAN) */
.dashboard-filter {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  max-width: 100%;
}

.range-tabs {
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
}

.range-tab {
  padding: 8px 16px;
  font-size: 0.92rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.range-tab:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.6);
}

.range-tab.active {
  background: #ffffff;
  color: #1d4ed8;
  font-weight: 800;
  border-color: #ffffff;
}

.custom-date-range {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  padding: 4px 10px;
  border-radius: 8px;
  border: none;
}

.custom-date-range input {
  border: 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: #0f172a;
}

.custom-date-range span {
  color: #777777;
  font-size: 0.85rem;
}

.filter-tab {
  width: 36px;
  min-width: 36px;
  padding: 8px;
}

.dashboard-topbar.sa-topbar {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 16px 24px !important;
  background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #1e40af 100%) !important;
  border-radius: 14px !important;
  min-height: 72px !important;
  height: auto !important;
  box-shadow: 0 4px 18px rgba(30, 58, 138, 0.22) !important;
}

.sa-topbar .dashboard-page-title {
  color: #ffffff !important;
  font-size: 1.35rem !important;
  font-weight: 800 !important;
  display: flex !important;
  align-items: center !important;
  gap: 10px !important;
  margin: 0 !important;
}

.sa-topbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sa-filter-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #ffffff;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 700;
  transition: all 0.2s ease;
  cursor: pointer;
}

.sa-filter-toggle-btn:hover,
.sa-filter-toggle-btn.is-open {
  background: #ffffff;
  color: #1d4ed8;
  border-color: #ffffff;
}

.sa-filter-toggle-btn.has-filters {
  background: #ffffff;
  color: #1d4ed8;
  border-color: #ffffff;
}

.sa-filter-count-badge {
  background: #1d4ed8;
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 800;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sa-refresh-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #ffffff;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sa-refresh-btn:hover {
  background: #ffffff;
  color: #1d4ed8;
  border-color: #ffffff;
}

/* Filter Drawer */
.sa-filter-drawer {
  background: #ffffff;
  border: 1.5px solid #bfdbfe;
  border-radius: 12px;
  padding: 16px 20px;
  margin-top: 10px;
  box-shadow: 0 10px 25px rgba(29, 78, 216, 0.12);
}

.sa-filter-drawer-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr) auto;
  gap: 12px;
  align-items: flex-end;
}

.sa-filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sa-filter-group label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #475569;
  margin: 0;
}

.sa-filter-actions {
  display: flex;
  gap: 6px;
}

/* Active Chips */
.sa-active-chips {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  padding: 8px 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
}

.sa-chips-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #1e40af;
}

.sa-chip {
  display: inline-flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid #93c5fd;
  color: #1d4ed8;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.sa-chip-del {
  background: none;
  border: none;
  color: #64748b;
  font-size: 1rem;
  line-height: 1;
  margin-left: 4px;
  cursor: pointer;
}

.sa-chip-del:hover {
  color: #dc2626;
}

.sa-chip-clear {
  background: none;
  border: none;
  color: #dc2626;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  margin-left: auto;
  text-decoration: underline;
}

/* Status dots */
.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.status-dot-danger { background: #dc2626; box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.2); }
.status-dot-amber { background: #d97706; box-shadow: 0 0 0 2px rgba(217, 119, 6, 0.2); }
.status-dot-muted { background: #64748b; box-shadow: 0 0 0 2px rgba(100, 116, 139, 0.2); }

.sa-panel { background: #fff; border: 1px solid #dbeafe; border-radius: 16px; padding: 22px; margin-bottom: 20px; box-shadow: 0 4px 18px rgba(29,78,216,.04); }
.sa-panel h2 { font-size: 18px; font-weight: 800; margin: 0 0 8px; color: #1e3a8a; }
.form-control { border-color: #bfdbfe; border-radius: 8px; font-size: 14px; min-height: 38px; }
/* KPI GRID & CARDS (MATCHES ADMIN & SUPERVISOR EXACTLY) */
.sa-kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.summary-card {
  background: #ffffff !important;
  border: 1.5px solid #dbeafe !important;
  padding: 18px 20px !important;
  border-radius: 16px !important;
  box-shadow: 0 2px 8px rgba(30, 58, 138, 0.04) !important;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease !important;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: left;
}

.summary-card:hover {
  transform: translateY(-2px) !important;
  border-color: #93c5fd !important;
  box-shadow: 0 6px 18px rgba(30, 58, 138, 0.1) !important;
}

.summary-card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.summary-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.icon-blue {
  background: #eff6ff !important;
  color: #1e40af !important;
  border: 1px solid #bfdbfe !important;
}

.icon-amber {
  background: #fffbeb !important;
  color: #d97706 !important;
  border: 1px solid #fde68a !important;
}

.icon-cyan {
  background: #f0f9ff !important;
  color: #0284c7 !important;
  border: 1px solid #bae6fd !important;
}

.icon-purple {
  background: #faf5ff !important;
  color: #7c3aed !important;
  border: 1px solid #e9d5ff !important;
}

.icon-green {
  background: #f0fdf4 !important;
  color: #059669 !important;
  border: 1px solid #bbf7d0 !important;
}

.icon-emerald {
  background: #ecfdf5 !important;
  color: #047857 !important;
  border: 1px solid #a7f3d0 !important;
}

.summary-label {
  color: #0f172a !important;
  font-size: 0.98rem !important;
  font-weight: 800 !important;
  margin-bottom: 6px;
}

.summary-bottom {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 4px;
}

.summary-value {
  font-size: 2.2rem !important;
  font-weight: 800 !important;
  color: #0f172a !important;
  line-height: 1;
}

.summary-sub {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 600;
}

.summary-arrow {
  color: #94a3b8;
  font-size: 1.15rem;
  font-weight: 700;
  transition: transform 0.2s ease, color 0.2s ease;
}

.summary-card:hover .summary-arrow {
  transform: translateX(3px);
  color: #1d4ed8;
}

.trend-badge {
  padding: 4px 9px !important;
  border-radius: 999px !important;
  font-size: 0.75rem !important;
  font-weight: 800 !important;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
}

.trend-neutral { background: #eff6ff !important; color: #1e40af !important; border: 1px solid #bfdbfe !important; }
.trend-amber { background: #fffbeb !important; color: #d97706 !important; border: 1px solid #fde68a !important; }
.trend-cyan { background: #f0f9ff !important; color: #0284c7 !important; border: 1px solid #bae6fd !important; }
.trend-active { background: #faf5ff !important; color: #7c3aed !important; border: 1px solid #e9d5ff !important; }
.trend-up { background: #f0fdf4 !important; color: #059669 !important; border: 1px solid #bbf7d0 !important; }
.trend-emerald { background: #ecfdf5 !important; color: #047857 !important; border: 1px solid #a7f3d0 !important; }

/* OPERATIONAL SUMMARY (ATTENTION CARDS) */
.operational-summary {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  width: 100%;
}

.overview-panel {
  flex: 1;
  min-width: 250px;
  background: #ffffff !important;
  padding: 16px 20px !important;
  border-radius: 14px !important;
  border: 1.5px solid #dbeafe !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(30, 58, 138, 0.03);
  cursor: pointer;
  transition: all 0.2s ease;
}

.overview-panel:hover {
  transform: translateY(-2px);
  border-color: #93c5fd;
  box-shadow: 0 6px 16px rgba(30, 58, 138, 0.08);
}

.overview-panel-left {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.overview-eyebrow {
  display: block;
  font-size: 0.8rem !important;
  color: #0369a1 !important;
  text-transform: uppercase;
  font-weight: 800 !important;
}

.overview-title {
  font-size: 0.98rem !important;
  color: #0f172a !important;
  font-weight: 800 !important;
  display: flex;
  align-items: center;
}

.overview-metrics {
  display: flex;
  align-items: center;
  gap: 8px;
}

.overview-metric {
  font-size: 0.95rem;
  color: #475569;
  font-weight: 600;
  white-space: nowrap;
}
.overview-metric b {
  color: #0f172a;
  font-weight: 800;
  font-size: 1.1rem;
}

.overview-arrow {
  color: #94a3b8;
  font-size: 1.15rem;
  font-weight: 700;
  transition: transform 0.2s ease;
}

.overview-panel:hover .overview-arrow {
  transform: translateX(3px);
  color: #1d4ed8;
}

/* CHARTS & ANALYTICS */
.sa-charts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.sa-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.sa-header-badge {
  padding: 4px 10px;
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 800;
}

.sa-trend {
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  height: 180px;
  gap: 12px;
  padding-top: 14px;
}

.sa-month {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;
  flex: 1;
  font-size: 12px;
  text-align: center;
}

.sa-month-val {
  font-size: 0.82rem;
  color: #0f172a;
  font-weight: 700;
  margin-bottom: 4px;
  line-height: 1;
  text-align: center;
}

.sa-month-bar-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  height: 105px;
  background: transparent !important;
}

.sa-month-bar {
  width: 70%;
  max-width: 36px;
  min-height: 4px;
  max-height: 80px;
  border-radius: 6px 6px 0 0;
  background: linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%);
  transition: height 0.3s ease;
}

.sa-month-lbl {
  color: #64748b;
  font-weight: 600;
  font-size: 0.8rem;
}

/* TOP BUILDINGS (REDESIGNED: CLEAN CARDS & COMPACT PROPORTIONAL BARS) */
.sa-building-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sa-building-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 12px;
  text-align: left;
}

.sa-building-card:hover {
  background: #eff6ff;
  border-color: #93c5fd;
  transform: translateX(2px);
  box-shadow: 0 4px 12px rgba(29, 78, 216, 0.08);
}

.sa-building-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.sa-rank-badge {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #1d4ed8 !important;
  color: #ffffff !important;
  font-size: 12px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(29, 78, 216, 0.3);
}

.sa-building-icon {
  color: #2563eb;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.sa-building-title {
  font-weight: 700;
  color: #0f172a;
  font-size: 0.92rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sa-building-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.sa-compact-bar-wrap {
  width: 110px;
  height: 8px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
  display: block;
}

.sa-compact-bar {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.sa-building-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 8px;
  background: #eff6ff;
  color: #1e40af;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  border: 1px solid #bfdbfe;
}

.sa-pct-text {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  margin-left: 2px;
}

.sa-row-chevron {
  font-size: 1.15rem;
  color: #94a3b8;
  font-weight: 700;
  line-height: 1;
}

.sa-building-card:hover .sa-row-chevron {
  color: #1d4ed8;
  transform: translateX(2px);
}

/* FOLLOW-UP LIST */
.sa-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.sa-followup-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sa-followup-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  color: inherit;
  text-decoration: none;
  transition: all 0.2s ease;
}

.sa-followup-card:hover {
  background: #eff6ff;
  border-color: #93c5fd;
  text-decoration: none;
  transform: translateX(2px);
}

.sa-followup-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.sa-followup-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
}

.sa-ticket-id {
  color: #1d4ed8;
  font-weight: 800;
}

.sa-dot {
  color: #94a3b8;
}

.sa-reporter {
  color: #334155;
  font-weight: 600;
}

.sa-followup-desc {
  color: #64748b;
  overflow-wrap: anywhere;
}

.sa-badge { display: inline-block; border-radius: 999px; padding: 4px 10px; font-size: 12px; font-weight: 700; background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
.sa-pending { background: #fef3c7; color: #92400e; border-color: #fde68a; }
.sa-assigned { background: #e0f2fe; color: #0369a1; border-color: #bae6fd; }
.sa-in_progress { background: #f3e8ff; color: #6b21a8; border-color: #e9d5ff; }
.sa-done, .sa-welldone { background: #d1fae5; color: #065f46; border-color: #a7f3d0; }
.sa-overdue { background: #fee2e2; color: #9f1239; border-color: #fecaca; }
.sa-home-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  border-bottom: 1.5px solid #e2e8f0;
  padding-bottom: 14px;
  margin-bottom: 16px;
}
.sa-tabs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  border-bottom: none;
  padding-bottom: 0;
  margin-bottom: 0;
}
.sa-tabs button {
  padding: 8px 16px;
  border: 1.5px solid #dbeafe;
  border-radius: 999px;
  background: #ffffff;
  color: #1e40af;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  transition: all 0.2s ease;
}
.sa-tabs button:hover {
  background: #eff6ff;
  border-color: #93c5fd;
}
.sa-tabs button.active {
  background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
  color: white;
  border-color: #1d4ed8;
  box-shadow: 0 4px 14px rgba(29, 78, 216, 0.25);
}
.sa-total-badge {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1e40af;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  padding: 4px 12px;
  border-radius: 999px;
}
.sa-table-wrap { overflow-x: auto; }
.sa-table {
  width: 100%;
  font-size: 14px;
  border-collapse: separate;
  border-spacing: 0;
}
.sa-table th {
  font-size: 0.82rem;
  background: #eff6ff;
  color: #1e40af;
  padding: 14px 16px;
  white-space: nowrap;
  font-weight: 800;
  border-bottom: 2px solid #bfdbfe;
}
.sa-table td {
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}
.sa-table tr:hover td {
  background: #f8fafc;
}
.sa-table small { display: block; margin-top: 3px; color: #64748b; }
.sa-ticket-number { font-weight: 800; color: #1d4ed8; font-size: 0.95rem; }
.sa-reporter-name { font-weight: 700; color: #0f172a; }
.sa-problem { max-width: 260px; min-width: 140px; overflow-wrap: anywhere; font-weight: 600; color: #0f172a; }
.sa-view-all-btn,
.sa-view-btn,
.sa-page .btn-outline-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  font-weight: 700;
  color: #1d4ed8 !important;
  background: #eff6ff !important;
  border: 1px solid #bfdbfe !important;
  border-radius: 8px;
  padding: 6px 14px;
  transition: all 0.15s ease;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  line-height: 1.25;

  &:hover {
    background: #1d4ed8 !important;
    color: #ffffff !important;
    border-color: #1d4ed8 !important;
    text-decoration: none;
    box-shadow: 0 2px 8px rgba(29, 78, 216, 0.2);
  }

  &:active {
    transform: scale(0.97);
  }
}
.sa-view-btn {
  padding: 5px 12px;
  font-size: 0.8rem;
}
.sa-pagination { display: flex; justify-content: flex-end; align-items: center; gap: 16px; margin-top: 20px; }
button:focus-visible, a:focus-visible { outline: 3px solid #1d4ed8; outline-offset: 3px; }
@media (max-width: 1200px) {
  .sa-kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .sa-page {
    padding-bottom: 85px !important;
  }

  .dashboard-sticky-area {
    margin-bottom: 12px !important;
    top: 0 !important;
    position: sticky !important;
    z-index: 100 !important;
  }

  .dashboard-topbar.sa-topbar {
    padding: 12px 14px !important;
    border-radius: 12px !important;
    gap: 8px !important;
    min-height: auto !important;
    box-shadow: 0 4px 14px rgba(30, 58, 138, 0.16) !important;
  }

  .sa-topbar .dashboard-page-title {
    font-size: 1.15rem !important;
    gap: 8px !important;
  }

  .sa-topbar .dashboard-title-icon {
    width: 20px !important;
    height: 20px !important;
  }

  .sa-topbar .dashboard-header-right {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    gap: 8px !important;
    width: 100% !important;
  }

  .sa-topbar .dashboard-search-box {
    flex: 1 1 auto !important;
    min-width: 0 !important;
    width: auto !important;
    height: 38px !important;
    padding: 4px 12px !important;
  }

  .sa-topbar .sa-topbar-actions {
    flex: 0 0 auto !important;
  }

  .sa-topbar .sa-filter-toggle-btn {
    white-space: nowrap !important;
    padding: 7px 12px !important;
    font-size: 0.82rem !important;
    height: 38px !important;
  }

  .sa-filter-drawer-grid {
    grid-template-columns: 1fr 1fr !important;
    gap: 10px !important;
  }

  .sa-filter-actions {
    grid-column: 1 / -1 !important;
    justify-content: flex-end !important;
    margin-top: 4px !important;
  }

  .sa-kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 10px !important;
    margin-bottom: 12px !important;
  }

  .summary-card {
    height: auto !important;
    min-height: 116px !important;
    padding: 12px 14px !important;
    border-radius: 14px !important;
    overflow: visible !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-between !important;
    box-shadow: 0 2px 8px rgba(30, 58, 138, 0.05) !important;
  }

  .summary-card-top {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    margin-bottom: 4px !important;
  }

  .summary-icon {
    width: 32px !important;
    height: 32px !important;
    border-radius: 9px !important;
    font-size: 15px !important;
  }

  .summary-label {
    font-size: 0.8rem !important;
    font-weight: 700 !important;
    color: #475569 !important;
    margin-top: 4px !important;
    margin-bottom: 4px !important;
    line-height: 1.25 !important;
  }

  .summary-value {
    font-size: 1.65rem !important;
    font-weight: 800 !important;
    line-height: 1.15 !important;
    color: #0f172a !important;
  }

  .sa-charts {
    grid-template-columns: 1fr !important;
    gap: 12px !important;
    margin-bottom: 12px !important;
  }

  .sa-panel {
    padding: 14px 14px !important;
    border-radius: 14px !important;
    margin-bottom: 12px !important;
  }

  .sa-panel h2 {
    font-size: 0.98rem !important;
  }

  .sa-trend {
    height: 125px !important;
    gap: 6px !important;
    padding-top: 6px !important;
  }

  .sa-month-bar-wrap {
    height: 90px !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: flex-end !important;
    align-items: center !important;
    background: transparent !important;
    border-radius: 0 !important;
    padding: 0 !important;
  }

  .sa-month-val {
    font-size: 0.78rem !important;
    font-weight: 700 !important;
    margin-bottom: 3px !important;
    line-height: 1 !important;
    color: #0f172a !important;
  }

  .sa-month-bar {
    width: 60% !important;
    max-width: 28px !important;
    max-height: 75px !important;
    border-radius: 5px 5px 0 0 !important;
  }

  .sa-month-lbl {
    font-size: 0.72rem !important;
  }

  .sa-building-card {
    padding: 10px 12px !important;
    border-radius: 10px !important;
    gap: 8px !important;
  }

  .sa-rank-badge {
    width: 20px !important;
    height: 20px !important;
    font-size: 11px !important;
  }

  .sa-building-title {
    font-size: 0.85rem !important;
  }

  .sa-compact-bar-wrap {
    width: 50px !important;
  }

  .sa-building-badge {
    font-size: 11px !important;
    padding: 3px 7px !important;
  }

  .sa-pct-text {
    display: none !important;
  }

  .sa-followup-card {
    padding: 12px 14px !important;
    border-radius: 10px !important;
    gap: 10px !important;
    align-items: flex-start !important;
    flex-direction: column !important;
  }

  .sa-followup-head {
    font-size: 0.88rem !important;
  }

  .sa-followup-desc {
    font-size: 0.8rem !important;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .sa-badge {
    font-size: 0.72rem !important;
    padding: 3px 8px !important;
    white-space: nowrap !important;
    align-self: flex-start !important;
  }

  .sa-table, .sa-table tbody, .sa-table tr, .sa-table td { display: block; }
  .sa-table thead { display: none; }
  .sa-table tr { padding: 14px; border: 1px solid #dbeafe; border-radius: 12px; margin-bottom: 12px; }
  .sa-table td { border: 0; padding: 7px 0; overflow-wrap: anywhere; }
  .sa-table td::before { content: attr(data-label); display: block; color: #64748b; font-size: 12px; margin-bottom: 3px; }
  .sa-table tr.match-highlight-active {
    border: 2px solid #1e40af !important;
    background-color: #fef3c7 !important;
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.25) !important;
  }
}

.sa-table tr.match-highlight-active td {
  background-color: #fef3c7 !important;
  border-top: 2px solid #1e40af !important;
  border-bottom: 2px solid #1e40af !important;
}
.sa-table tr.match-highlight-active td:first-child {
  border-left: 2px solid #1e40af !important;
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}
.sa-table tr.match-highlight-active td:last-child {
  border-right: 2px solid #1e40af !important;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}
</style>
